import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Box, Button, ButtonGroup, Paper, Skeleton, Typography, Snackbar, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CompareApprovalsProps, GqlCreditSubmissionApproval } from '../types';
import { useCompareApprovals } from '../hooks';
import { ApprovalTermsTable, ConditionsSection, ReviewExtractedTerms } from '../components';
import FileUploadModal from '../../../pages/VendorOpportunity/LenderSubmissionFileUploadModal';
import { DocumentContext } from '../../../contexts/DocumentContext';
import { useAiDecisionParsingCompleted } from '../../../hooks/useSubscriptions';

export const CompareApprovals: React.FC<CompareApprovalsProps> = ({ vo, voId, dynamicsVoId, prescreenContent }) => {
  const { activeTab, setActiveTab, approvals, rawCreditSubmissions, badgesByRow, loading, refetch } =
    useCompareApprovals(dynamicsVoId);

  const [uploadOpen, setUploadOpen] = useState(false);
  const documentContext = useContext(DocumentContext);

  // AI parsing subscription state
  const [aiParsingCsId, setAiParsingCsId] = useState<string | null>(null);
  const [aiParsingInProgress, setAiParsingInProgress] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'error' | 'info'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  // When a document is uploaded with AI parsing, capture the credit submission ID to subscribe.
  // After consuming it, clear aiParsingCreditSubmissionId from the context so a remount
  // (e.g. switching tabs) won't re-trigger the snackbar.
  const lastDoc = (documentContext as any)?.lastDoc;
  const setLastDocument = (documentContext as any)?.setLastDocument;
  useEffect(() => {
    const csId = lastDoc?.aiParsingCreditSubmissionId;
    if (csId) {
      setAiParsingCsId(csId);
      setAiParsingInProgress(true);
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'AI is analyzing the decision letter. You will be notified when the results are ready.',
      });
      // Clear the trigger so remounting doesn't re-fire
      if (setLastDocument) {
        const { aiParsingCreditSubmissionId, ...rest } = lastDoc;
        setLastDocument(rest);
      }
    }
  }, [lastDoc, setLastDocument]);

  // Subscribe to AI parsing result
  const { data: aiParsingData } = useAiDecisionParsingCompleted(aiParsingCsId);

  useEffect(() => {
    if (!aiParsingData?.aiDecisionParsingCompleted) return;

    const result = aiParsingData.aiDecisionParsingCompleted;
    setAiParsingInProgress(false);

    if (result.success) {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'AI decision letter parsing completed successfully. Refreshing approvals...',
      });
      // Show skeleton while refetching
      setRefetching(true);
      refetch().finally(() => setRefetching(false));
    } else {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'An error occurred when trying to Parse with AI. Please review the decision letter manually.',
      });
    }

    // Clear the subscription target so we don't re-subscribe
    setAiParsingCsId(null);
  }, [aiParsingData, refetch]);

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // When a lender column is clicked, show the review view for that parsed decision
  const [reviewTarget, setReviewTarget] = useState<{
    cs: GqlCreditSubmissionApproval;
    parsedDecisionId: string;
  } | null>(null);

  const handleEditApproval = useCallback(
    (parsedDecisionId: string) => {
      const cs = rawCreditSubmissions.find(c => c.creditDecisionParsedData?.parsedDecisionId === parsedDecisionId);
      if (cs) {
        setReviewTarget({ cs, parsedDecisionId });
      }
    },
    [rawCreditSubmissions]
  );

  const handleReviewSaveComplete = useCallback(() => {
    setRefetching(true);
    refetch().finally(() => setRefetching(false));
  }, [refetch]);

  if (!loading && approvals.length === 0 && prescreenContent === undefined) {
    return null;
  }

  // Review view (inline, replaces the table)
  if (reviewTarget) {
    return (
      <Paper sx={{ p: 3 }}>
        <ReviewExtractedTerms
          creditSubmission={reviewTarget.cs}
          parsedDecisionId={reviewTarget.parsedDecisionId}
          onBack={() => setReviewTarget(null)}
          onSaveComplete={handleReviewSaveComplete}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header: Tabs + Upload button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <ButtonGroup
          variant="outlined"
          size="small"
          sx={{
            borderRadius: '8px',
            overflow: 'hidden',
            '& .MuiButtonGroup-grouped': {
              minWidth: 150,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.8rem',
              py: 1,
              px: 2.5,

              borderColor: 'primary.main',
            },
          }}
        >
          {prescreenContent !== undefined && (
            <Button
              variant={activeTab === 'prescreen' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('prescreen')}
              sx={{ ml: 1 }}
            >
              Prescreen Results
            </Button>
          )}
          {approvals.length > 0 && (
            <Button
              variant={activeTab === 'approvals' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('approvals')}
              sx={{ mr: 1 }}
            >
              Approvals
            </Button>
          )}
        </ButtonGroup>

        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          size="small"
          disabled={!vo || aiParsingInProgress}
          onClick={() => setUploadOpen(true)}
        >
          Upload Decision Letter
        </Button>
      </Box>

      {vo && (
        <FileUploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          vo={vo}
          voId={voId}
          relatedTo="submission"
          fixedDocType="decision letter"
          aiEnabledParsing={true}
        />
      )}

      {/* Tab Content */}
      {activeTab === 'approvals' && (
        <Box>
          <Typography variant="caption" fontSize="20px" fontWeight={700} sx={{ mb: 2 }}>
            Compare Approvals
          </Typography>

          {loading || refetching ? (
            <Box>
              <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 1 }} />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1, mb: 0.5 }} />
              ))}
            </Box>
          ) : (
            <>
              <ApprovalTermsTable approvals={approvals} badgesByRow={badgesByRow} onEdit={handleEditApproval} />
              <ConditionsSection approvals={approvals} conditionsBadges={badgesByRow['conditions'] ?? {}} />
            </>
          )}
        </Box>
      )}

      {activeTab === 'prescreen' && (
        <Box>
          {prescreenContent ?? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Prescreen results will be available here.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* AI parsing info / result notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'info' ? null : 8000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          icon={snackbar.severity === 'info' ? <InfoOutlinedIcon fontSize="inherit" /> : undefined}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};
